import { a as getErrorNativeCode, c as unknownToError, i as getErrorMessageOrDefault, n as getErrorCode, o as isErrorWithSystemCode, r as getErrorMessage, s as sanitizeFramePath, t as computeErrorFingerprint } from "./errors-CPWzJd3r.js";

//#region src/constants.ts
const HTTP_HEADER_SIZE = 16384;
const DEBUG_PORT = "9222";
const VCREDIST_URL = "https://learn.microsoft.com/en-US/cpp/windows/latest-supported-vc-redist";

//#endregion
//#region src/Debouncer.ts
/**
* management function. Prevents a function from being called too often
* and, for function returning a promise, it ensures that it's not run
* again (through this Debouncer) before the promise is resolved.
*/
var GenericDebouncer = class {
	mDebounceMS;
	mFunc;
	mTimer;
	mCallbacks = [];
	mAddCallbacks = [];
	mRunning = false;
	mReschedule = "no";
	mArgs = [];
	mResetting;
	mTriggerImmediately;
	mRetrigger = false;
	#setTimeoutFunc;
	#clearTimeoutFunc;
	/**
	* constructor
	* @param func the function to call when the timer expired
	* @param debounceMS the (minimum) time between two calls
	* @param reset if true (the default) the time is reset with every
	*              time schedule gets called. This means if the debouncer
	*              is triggered regularly in less than debounceMS it never
	*              gets run.
	* @param triggerImmediately if true, the debouncer will trigger immediately
	*                           when first called and then not be called again
	*                           until the timer expires. Otherwise (the default)
	*                           the initial call is delay.
	*/
	constructor(setTimeoutFunc, clearTimeoutFunc, func, debounceMS, reset, triggerImmediately = false) {
		this.#setTimeoutFunc = setTimeoutFunc;
		this.#clearTimeoutFunc = clearTimeoutFunc;
		this.mResetting = reset !== false;
		this.mFunc = func;
		this.mDebounceMS = debounceMS;
		this.mTriggerImmediately = triggerImmediately;
	}
	/**
	* schedule the function and invoke the callback once that is done
	* @param callback the callback to invoke upon completion
	* @param args the arguments to pass to the function. When the timer expires
	*             and the function actually gets invoked, only the last set of
	*             parameters will be used
	*/
	schedule(callback, ...args) {
		if (callback !== void 0 && callback !== null) this.mCallbacks.push(callback);
		this.mArgs = args;
		if (this.mTriggerImmediately && this.mTimer === void 0) this.run();
		else {
			const doReset = this.mTimer !== void 0 && this.mResetting;
			if (doReset) this.clear();
			if (this.mTriggerImmediately) {
				this.mRetrigger = true;
				if (doReset) this.startTimer();
			} else if (this.mRunning) {
				if (this.mReschedule !== "immediately") this.mReschedule = "yes";
			} else if (this.mTimer === void 0) this.startTimer();
		}
	}
	/**
	* run the function immediately without waiting for the timer
	* to run out. (It does cancel the timer though and invokes all
	* scheduled callbacks)
	*
	* @param {(err: Error) => void} callback
	* @param {...any[]} args
	*
	* @memberOf Debouncer
	*/
	runNow(callback, ...args) {
		if (this.mTimer !== void 0) this.clear();
		if (callback !== void 0 && callback !== null) this.mCallbacks.push(callback);
		this.mArgs = args;
		if (this.mRunning) this.mReschedule = "immediately";
		else this.run();
	}
	/**
	* wait for the completion of the current timer without scheduling it.
	* if the function is not scheduled currently the callback will be
	* called (as a success) immediately.
	* This does not reset the timer
	*
	* @param {(err: Error) => void} callback
	* @param {boolean} immediately if set (default is false) the function gets called
	*                              immediately instead of awaiting the timer
	*
	* @memberOf Debouncer
	*/
	wait(callback, immediately = false) {
		if (this.mTimer === void 0 && !this.mRunning) {
			callback(null);
			return;
		}
		this.mAddCallbacks.push(callback);
		if (immediately && !this.mRunning) {
			this.clear();
			this.run();
		}
	}
	clear() {
		if (this.mTimer) this.#clearTimeoutFunc(this.mTimer);
		this.mTimer = void 0;
	}
	run() {
		const callbacks = this.mCallbacks;
		this.mCallbacks = [];
		const args = this.mArgs;
		this.mArgs = [];
		this.mTimer = void 0;
		let prom;
		try {
			prom = this.mFunc(...args);
		} catch (err) {
			prom = unknownToError(err);
		}
		if (prom instanceof Error) this.invokeCallbacks(callbacks, prom);
		else {
			this.mRunning = true;
			Promise.resolve(prom).then(() => this.invokeCallbacks(callbacks, null)).catch((err) => this.invokeCallbacks(callbacks, err)).finally(() => {
				this.mRunning = false;
				if (this.mReschedule === "immediately") {
					this.mReschedule = "no";
					this.run();
				} else if (this.mReschedule === "yes") {
					this.mReschedule = "no";
					this.reschedule();
				}
			});
		}
		if (this.mTriggerImmediately) this.startTimer();
	}
	reschedule() {
		if (this.mTimer !== void 0 && this.mResetting) this.clear();
		if (this.mTimer === void 0) this.startTimer();
	}
	invokeCallbacks(localCallbacks, err) {
		localCallbacks.forEach((cb) => cb(err));
		this.mAddCallbacks.forEach((cb) => cb(err));
		this.mAddCallbacks = [];
	}
	startTimer() {
		this.mTimer = this.#setTimeoutFunc(() => {
			this.mTimer = void 0;
			if (!this.mTriggerImmediately || this.mRetrigger) {
				this.mRetrigger = false;
				if (this.mRunning) this.mReschedule = "immediately";
				else this.run();
			}
		}, this.mDebounceMS);
	}
};

//#endregion
export { DEBUG_PORT, GenericDebouncer, HTTP_HEADER_SIZE, VCREDIST_URL, computeErrorFingerprint, getErrorCode, getErrorMessage, getErrorMessageOrDefault, getErrorNativeCode, isErrorWithSystemCode, sanitizeFramePath, unknownToError };
//# sourceMappingURL=index.js.map